<template>
  <boss-full-effect v-if="isBoss" embedded/>
  <div v-else class="efficiency-page">
    <van-nav-bar :border="false" title="全效">
      <div slot="right" class="nav-tools">
        <button v-if="!isBoss" class="history-tool" title="日志历史" aria-label="日志历史" @click="$router.push({name:'EfficiencyHistory'})">
          <van-icon name="clock-o"/>
        </button>
        <button v-if="employee.supervisor || isBoss" class="supervisor-tool" title="主管工作台" aria-label="主管工作台"
                @click="$router.push({name:'SupervisorHome'})">
          <van-icon name="friends-o"/>
        </button>
      </div>
    </van-nav-bar>
    <section class="employee-card">
      <div class="avatar">{{ (employee.employeeName || "全").slice(-1) }}</div>
      <div class="employee-main"><h1>{{ employee.employeeName || "当前员工" }}</h1>
        <p>{{ employee.departmentName || "安全事业部" }}</p></div>
      <span class="position-tag">{{ employee.positionName || "销售" }}</span>
    </section>
    <section v-if="unreadTaskCount||activeTaskCount" class="task-notice" @click="$router.push({name:'EfficiencyTasks'})">
      <div class="task-icon">
        <van-icon name="volume-o"/>
        <i v-if="unreadTaskCount">{{ unreadTaskCount }}</i></div>
      <span><b>{{ latestTaskTitle }}</b><small>{{ activeTaskCount }} 项任务待处理，完成结果将计入绩效</small></span>
      <van-icon name="arrow"/>
    </section>
    <section v-if="activeCorrectionCount" class="task-notice correction-notice" @click="$router.push({name:'EfficiencyCorrections'})">
      <div class="task-icon">
        <van-icon name="guide-o"/>
        <i>{{ activeCorrectionCount }}</i></div>
      <span><b>主管纠偏与赋能建议</b><small>查看依据、反馈意见并回传执行结果</small></span>
      <van-icon name="arrow"/>
    </section>
    <section v-if="isFinance" class="task-notice finance-notice" @click="$router.push({name:'FinanceQuantitativeReviews'})">
      <div class="task-icon">
        <van-icon name="balance-o"/>
        <i v-if="financePendingCount">{{ financePendingCount }}</i></div>
      <span><b>销售/品牌量化审核</b><small>{{ financePendingCount ? financePendingCount + ' 条待核实回款与量化指标' : '查看待审与已处理量化结果' }}</small></span>
      <van-icon name="arrow"/>
    </section>
    <section v-if="isFinance" class="task-notice settlement-notice" @click="$router.push({name:'FinanceSettlements'})">
      <div class="task-icon">
        <van-icon name="gold-coin-o"/>
        <i v-if="financeSettlementPending">{{ financeSettlementPending }}</i></div>
      <span><b>月度绩效结算</b><small>{{ financeSettlementPending ? financeSettlementPending + ' 人待确认纳入结算' : '查看待确认/已确认月度奖惩' }}</small></span>
      <van-icon name="arrow"/>
    </section>
    <section class="action-panel">
      <div class="date-block"><strong>{{ todayLabel }}</strong><span>{{ weekdayLabel }}</span></div>
      <div class="action-list">
        <div class="action-row">
          <div class="action-left"><b><span class="action-title">今日日志</span><span v-if="dailyRejected && dailyRejectReason" class="reject-reason"><span
            class="reject-reason-text">（{{ dailyRejectReason }}）</span></span></b><span class="status-label"
                                                                                        :class="dailyRejected ? 'reject' : dailySubmitted ? 'done' : todayWorkday ? 'pending' : 'rest-text'">{{
              dailyRejected ? "已驳回待提交" : dailySubmitted ? "已提交" : todayWorkday ? "待提交" : "今日休息"
            }}</span></div>
          <van-button class="action-btn" round size="small" color="#D32F2F" :disabled="dailySubmitted || !todayWorkday"
                      @click="openLog('DAILY', today)">{{
              dailyRejected ? "去修改" : dailySubmitted ? "已完成" : todayWorkday ? "去填写" : "无需提交"
            }}
          </van-button>
        </div>
        <div class="action-row">
          <div><b>本周总结</b><span class="status-label" :class="weeklySubmitted ? 'done' : 'pending'">{{
              weeklySubmitted ? "已提交" : "待提交"
            }}</span></div>
          <van-button class="action-btn" round size="small" plain color="#D32F2F" :disabled="weeklySubmitted" @click="openLog('WEEKLY', today)">
            {{ weeklySubmitted ? "已完成" : "去填写" }}
          </van-button>
        </div>
      </div>
    </section>
    <section v-if="hardMetrics.length" class="kpi-panel">
      <header>
        <div><h2>本月核心指标</h2>
          <p>{{ ruleLabel }}</p></div>
        <span :class="performance.hardTargetAchieved ? 'achieved' : 'progressing'">{{ performance.hardTargetAchieved ? "已达标" : "进行中" }}</span>
      </header>
      <div class="kpi-list">
        <div v-for="metric in hardMetrics" :key="metric.code" class="kpi-item">
          <div class="kpi-copy"><b>{{ metric.name }}</b><span>{{ formatMetric(metric.actual) }} / {{ formatMetric(metric.target) }} {{ metric.unit }}<template
            v-if="metric.weight"> · {{ formatWeight(metric.weight) }}</template></span></div>
          <div class="progress-track"><i :style="{width: progressWidth(metric)}"></i></div>
          <strong>{{ formatRate(metric.completionRate) }}</strong>
        </div>
      </div>
    </section>
    <section v-if="configurationWarnings.length" class="notice-panel"><p v-for="item in configurationWarnings" :key="item">{{ item }}</p></section>
    <section v-if="showEmployeeGrowthSections" class="quick-panel">
      <button @click="$router.push({name:'EfficiencyWorkPoints'})">
        <van-icon name="todo-list-o"/>
        <span><b>我的工作要点</b><small>查看目标依据、验收标准并反馈执行结果</small></span>
        <van-icon name="arrow"/>
      </button>
      <button @click="$router.push({name:'EfficiencyGrowth'})">
        <van-icon name="chart-trending-o"/>
        <span><b>能力成长</b><small>岗位能力、学习推荐和实际成长效果</small></span>
        <van-icon name="arrow"/>
      </button>
      <button @click="$router.push({name:'EfficiencyLeaderboard'})">
        <van-icon name="bar-chart-o"/>
        <span><b>绩效排行榜</b><small>查看公司榜与部门榜</small></span>
        <van-icon name="arrow"/>
      </button>
    </section>
    <section v-if="employee.supervisor" class="personal-entry"><h2>我的工作入口</h2>
      <div>
        <button @click="$router.push({name:'SupervisorHome'})">
          <van-icon name="manager-o"/>
          主管工作台
        </button>
      </div>
    </section>
    <section v-if="!employee.supervisor" class="execution-entry">
      <header><h2>我的战略执行</h2>
        <p>从任务到今天该做什么</p></header>
      <button @click="$router.push({name:'EfficiencyTasks'})">
        <van-icon name="orders-o"/>
        <span><b>我的任务</b><small>查看项目目标、任务要求和验收标准</small></span>
        <van-icon name="arrow"/>
      </button>
      <button @click="$router.push({name:'EfficiencyWorkPoints'})">
        <van-icon name="todo-list-o"/>
        <span><b>我的工作要点</b><small>根据任务生成今日行动并回传完成结果</small></span>
        <van-icon name="arrow"/>
      </button>
      <button @click="$router.push({name:'EfficiencyCorrections'})">
        <van-icon name="guide-o"/>
        <span><b>纠偏行动</b><small>{{ activeCorrectionCount ? activeCorrectionCount + ' 项待回应或执行' : '查看历史纠偏、执行证据和验收结果' }}</small></span>
        <van-icon name="arrow"/>
      </button>
      <button @click="$router.push({name:'EfficiencyGrowth'})">
        <van-icon name="chart-trending-o"/>
        <span><b>我的能力画像</b><small>查看岗位能力差距、成长建议和实际改善</small></span>
        <van-icon name="arrow"/>
      </button>
    </section>
    <section v-if="showEmployeeGrowthSections && improvementSuggestions.length" class="suggestion-panel">
      <header>
        <div><h2>改进建议</h2>
          <p>根据本月低分项自动匹配学习资料</p></div>
        <van-icon name="bulb-o"/>
      </header>
      <article v-for="item in improvementSuggestions" :key="item.metricCode">
        <div><b>{{ suggestionTitle(item) }}</b><span>{{ suggestionReason(item) }}</span></div>
        <button v-if="item.materials.length" @click="openMaterial(item)">学习 {{ item.materials.length }} 项资料</button>
        <em v-else>资料待运营补充</em></article>
    </section>
    <section class="calendar-card">
      <header>
        <button aria-label="上个月" @click="changeMonth(-1)">
          <van-icon name="arrow-left"/>
        </button>
        <strong>{{ currentYear }} 年 {{ currentMonth }} 月</strong>
        <button aria-label="下个月" @click="changeMonth(1)">
          <van-icon name="arrow"/>
        </button>
      </header>
      <div class="week-row"><span v-for="name in weekdays" :key="name">{{ name }}</span></div>
      <div class="calendar-grid">
        <button v-for="day in calendarDays" :key="day.key" :disabled="!day.current || day.future || !day.workday || day.submitted"
                :class="day.classes" @click="openLog('DAILY', day.date)"><span>{{ day.label }}</span><i v-if="day.submitted" class="check">✓</i><i
          v-else-if="day.missing" class="dot"></i><i v-else-if="day.current && !day.workday" class="rest-mark">休</i></button>
      </div>
      <footer class="legend"><span><i class="submitted"></i>已提交</span><span><i class="missing"></i>已缺交</span><span><i
        class="rest"></i>休息日</span><span><i class="today"></i>今天</span></footer>
    </section>
    <section class="shared-rank-entries">
      <button type="button" @click="$router.push('/efficiency/boss/all-worklogs')"><b>日志综合榜</b></button>
      <button type="button" @click="$router.push({name:'BossProjectValueRanking'})"><b>项目排序</b><small>按价值排序</small></button>
    </section>
    <section v-if="employee.supervisor" class="shared-rank-entries">
      <button type="button" @click="$router.push({name:'BossDepartmentData'})">
        <b>部门数据</b><small>按部门维度</small>
      </button>
      <button type="button" @click="$router.push({name:'BossCustomerData'})">
        <b>客户数据</b><small>按客户维度</small>
      </button>
    </section>
    <van-loading v-if="loading" class="page-loading" color="#B71C1C">加载全效数据</van-loading>
  </div>
</template>

<script>
/**
 * 全效首页（员工工作台）：日历、日报/周报入口、KPI、任务通知与改进建议。
 * Boss 身份仅展示管理入口；普通员工 keep-alive 返回时会刷新 workspace。
 */
import coreapi from "@/utils/coreapi";
import { isPageCacheFresh, readPageCache, writePageCache } from "@/utils/pageCache";
import BossFullEffect from "@/views/boss/FullEffect.vue";

export default {
  name: "Efficiency",
  components: {BossFullEffect},
  data() {
    const now = new Date();
    return {
      loading: false,
      initialized: false,
      showEmployeeGrowthSections: false,
      employee: {},
      entries: [],
      workCalendar: [],
      performance: {},
      configurationWarnings: [],
      improvementSuggestions: [],
      tasks: [],
      notifications: [],
      corrections: [],
      logHistory: [],
      financePendingCount: 0,
      financeSettlementPending: 0,
      viewDate: new Date(now.getFullYear(), now.getMonth(), 1),
      weekdays: ["一", "二", "三", "四", "五", "六", "日"]
    };
  },
  computed: {
    // Boss / 总经理：隐藏个人日志区，只保留管理入口
    // 只信后端返回的 Session（employee.boss / positionFamily），localStorage 残留（上一个登录账号写入的值）
    // 会导致脏读——同浏览器切换账号时把普通员工误判成 Boss，直接跳过日志提交视图。
    isBoss() {
      return this.employee.boss === true || this.employee.positionFamily === "GENERAL_MANAGER";
    },
    isFinance() {
      return this.employee.positionFamily === "FINANCE";
    },
    today() {
      return this.formatDate(new Date());
    },
    todayLabel() {
      const d = new Date();
      return `${d.getMonth() + 1}月${d.getDate()}日`;
    },
    weekdayLabel() {
      return ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"][new Date().getDay()];
    },
    currentYear() {
      return this.viewDate.getFullYear();
    }, currentMonth() {
      return this.viewDate.getMonth() + 1;
    },
    monthKey() {
      return `${this.currentYear}-${String(this.currentMonth).padStart(2, "0")}`;
    },
    dailySubmitted() {
      return this.isSubmitted(this.today, "DAILY");
    },
    // 已驳回：后端将退回的日志状态置为 DRAFT（普通草稿仅缓存不入库），故持久化 DRAFT 即表示已驳回待提交
    dailyRejected() {
      return this.isRejected(this.today, "DAILY");
    },
    // 驳回原因：取历史中同日期 DAILY 且复核动作为 RETURN 的 reason
    dailyRejectReason() {
      const item = this.logHistory.find(h => h.workDate === this.today && h.cadence === "DAILY");
      if (!item) return "";
      const latest = (item.reviewHistory || []).find(r => r.action === "RETURN");
      if (latest) return latest.reason || "";
      return (item.review && item.review.action === "RETURN" && item.review.reason) || "";
    },
    // 前端「本周总结」对应后端 MONTHLY cadence 存储
    weeklySubmitted() {
      return this.isSubmitted(this.today, "MONTHLY");
    },
    todayWorkday() {
      const day = this.workCalendar.find(item => item.date === this.today);
      return day ? day.workday : true;
    },
    hardMetrics() {
      return (this.performance.metrics || []).filter(item => item.nature === "HARD_QUANTITATIVE");
    },
    unreadTaskCount() {
      return this.notifications.filter(item => !item.readAt).length;
    },
    activeTaskCount() {
      return this.tasks.filter(item => ["ASSIGNED", "IN_PROGRESS", "RETURNED"].includes(item.status)).length;
    },
    activeCorrectionCount() {
      return this.corrections.filter(item => item.status !== "VERIFIED").length;
    },
    latestTaskTitle() {
      const notice = this.notifications.find(item => !item.readAt);
      return notice ? notice.title + "：" + notice.content : "重大/临时任务";
    },
    // 多指标时按 hardRule（OR/AND）展示达标文案
    ruleLabel() {
      const count = this.hardMetrics.length;
      if (count <= 1) return "单项指标独立考核";
      return this.performance.hardRule === "OR" ? "任一核心指标达标" : "全部核心指标需同时达标";
    },
    /** 42 格月历：休息日、缺交、今日可填状态 */
    calendarDays() {
      const first = new Date(this.currentYear, this.currentMonth - 1, 1), offset = (first.getDay() + 6) % 7;
      const start = new Date(this.currentYear, this.currentMonth - 1, 1 - offset);
      return Array.from({length: 42}, (_, index) => {
        const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index), key = this.formatDate(date);
        const current = date.getMonth() === this.currentMonth - 1, future = key > this.today,
          rule = this.workCalendar.find(item => item.date === key), workday = !current || !rule ? true : rule.workday,
          submitted = this.isSubmitted(key, "DAILY"), missing = current && workday && !future && !submitted && key !== this.today;
        return {
          key,
          date: key,
          label: date.getDate(),
          current,
          future,
          workday,
          submitted,
          missing,
          classes: {outside: !current, future, rest: current && !workday, submitted, missing, today: key === this.today && !submitted}
        };
      });
    }
  },
  activated() {
    if (!this._shown) {
      this._shown = true;
      return;
    }
    if (!this.initialized || this.isBoss || this._booting) return;
    if (isPageCacheFresh(readPageCache("efficiency:" + this.monthKey))) return;
    this.loadPrimary().then(() => this.loadSecondary()).catch(e => this.$toast.fail(e.message));
  },
  //  mounted() {  if (this.needRedirectToLogin()) return; this.initialize(); },
  created() {
    const query = this.$route.query;
    console.log('WebView 登录参数：', query);

    // const employeeNo = String(query.data || '').trim();
    // console.log('WebView 登录参数：', data);


    const raw = this.getUrlDataParam();
    console.log('[efficiency] raw data =', raw.length);
    if (raw) {
      // 命中 URL 登录态：解析 data 查询串并写入 localStorage（复刻 OKR 登录后写入的字段）
      let qs = raw;
      console.log('此处有值1')
      try {
        qs = decodeURIComponent(qs);
      } catch (e) { /* 已是明文查询串 */
      }
      console.log('此处有值2')
      // 兼容双编码场景：解码后仍含 %3D/%26 则再解一层
      if (/%3D|%26/i.test(qs)) {
        try {
          qs = decodeURIComponent(qs);
        } catch (e) {
        }
      }
      console.log('[efficiency] 命中 URL 登录态 data =', qs);
      // 1) 按需求原样整体赋值 localStorage
      localStorage.setItem('okrLoginData', qs);
      // 2) 解析 key=value&key=value 查询串，逐字段写入 localStorage（含 isBoss 等角色字段）
      const obj = {};
      qs.split('&').forEach(pair => {
        if (!pair) return;
        const i = pair.indexOf('=');
        const k = i >= 0 ? pair.slice(0, i) : pair;
        const v = i >= 0 ? pair.slice(i + 1) : '';
        if (k) {
          obj[k] = v;
          localStorage.setItem(k, v);
        }
      });
      // 3) 写入 synchronize 实际依赖的 Core 系列键，保证原有流程可正常渲染
      if (obj.UserSysno) localStorage.setItem('CoreEmployeeName', obj.UserSysno);
      if (obj.GovDepSysno) {
        localStorage.setItem('GovDepSysno', obj.GovDepSysno);
        localStorage.setItem('CoreDepartmentKey', obj.GovDepSysno);
      }
    }
    if (this.needRedirectToLogin()) return;
    this.initialize();
    // const tenantId = String(query.tenantId || '').trim();
    // const token = String(query.token || '').trim();

    // if (!employeeNo || !tenantId || !token) return;

    // localStorage.setItem('employeeNo', employeeNo);
    // localStorage.setItem('tenantId', tenantId);
    // localStorage.setItem('token', token);

    // this.$router.replace('/efficiency').then(() => {
    //   window.location.reload();
    // });
  },
  methods: {
    /** 小程序入口：URL 带 ?data= 时直接写入登录态到 localStorage，无则走原有流程 */
    /** 从 location.hash 提取 data 参数原始串（hash 路由下 %26 仍保留编码，避免被 vue-router 解析成 & 截断） */
    getUrlDataParam() {
      const hash = window.location.hash || '';
      const m = hash.match(/[?&]data=([^&]*)/);
      return m ? m[1] : '';
    },
    /** 无 URL 入参且本地无登录态时，跳转到登录页；返回 true 表示已跳转 */
    needRedirectToLogin() {
      const hasParam = !!this.getUrlDataParam();
      if (hasParam) return false; // 有入参：applyUrlLoginData 已写入登录态，走正常流程
      if (localStorage.getItem('UserSysno')) return false; // 本地已有登录态
      console.log('[efficiency] 无登录态且未带 data 参数，跳转登录页');
      this.$router.replace('/login');
      return true;
    },
    /** 首次进入：有缓存先画出来。session 与工作台并行，通知和纠偏随后补上。 */
    async initialize() {
      if (this._booting) return;
      this._booting = true;
      const painted = this.restoreEfficiency(this.monthKey);
      this.loading = !painted;
      try {
        const sessionTask = this.ensureSession();
        if (!painted && !this.hasKnownIdentity()) await sessionTask;
        if (this.isBoss) {
          this.initialized = true;
          const follow = isPageCacheFresh(readPageCache("session")) ? Promise.resolve() : this.fetchSession();
          follow.then(() => {
            if (!this.isBoss) return this.loadPrimary().then(() => this.loadSecondary());
          }).catch(() => {});
          return;
        }
        await this.loadPrimary();
        this.initialized = true;
        this.loadSecondary().catch(() => {});
      } catch (e) {
        if (!painted) this.$toast.fail(e.message);
      } finally {
        this.loading = false;
        this._booting = false;
      }
    },
    hasKnownIdentity() {
      return !!(readPageCache("session") || localStorage.getItem("CorePositionFamily") || localStorage.getItem("CoreEmployeeName"));
    },
    restoreEfficiency(month) {
      const cached = readPageCache("efficiency:" + month);
      if (!cached || !cached.data) {
        this.seedEmployeeFromLocal();
        return false;
      }
      const data = cached.data;
      this.employee = data.employee || this.employee;
      this.entries = data.entries || [];
      this.workCalendar = data.workCalendar || [];
      this.performance = data.performance || {};
      this.configurationWarnings = data.configurationWarnings || [];
      this.improvementSuggestions = data.improvementSuggestions || [];
      this.tasks = data.tasks || [];
      this.notifications = data.notifications || [];
      this.corrections = data.corrections || [];
      this.logHistory = data.logHistory || [];
      this.financePendingCount = data.financePendingCount || 0;
      this.financeSettlementPending = data.financeSettlementPending || 0;
      return true;
    },
    seedEmployeeFromLocal() {
      if (this.employee && this.employee.employeeName) return;
      const name = localStorage.getItem("CoreEmployeeName");
      const family = localStorage.getItem("CorePositionFamily");
      if (!name && !family) return;
      this.employee = {
        employeeName: name || "",
        departmentName: localStorage.getItem("CoreDepartmentName") || "",
        positionName: localStorage.getItem("CorePositionName") || "",
        positionFamily: family || "",
        boss: localStorage.getItem("CoreIsBoss") === "true" || localStorage.getItem("isBoss") === "1",
        supervisor: localStorage.getItem("CoreSupervisor") === "true",
        brandPost: localStorage.getItem("CoreBrandPost") || ""
      };
    },
    persistEfficiency() {
      writePageCache("efficiency:" + this.monthKey, {
        employee: this.employee,
        entries: this.entries,
        workCalendar: this.workCalendar,
        performance: this.performance,
        configurationWarnings: this.configurationWarnings,
        improvementSuggestions: this.improvementSuggestions,
        tasks: this.tasks,
        notifications: this.notifications,
        corrections: this.corrections,
        logHistory: this.logHistory,
        financePendingCount: this.financePendingCount,
        financeSettlementPending: this.financeSettlementPending
      });
    },
    async ensureSession() {
      const cached = readPageCache("session");
      if (cached && cached.data) this.applyEmployeeSession(cached.data);
      else this.seedEmployeeFromLocal();
      if (cached && cached.data && isPageCacheFresh(cached)) return cached.data;
      const task = this.fetchSession();
      if (this.hasKnownIdentity()) {
        task.catch(() => {});
        return cached && cached.data;
      }
      return task;
    },
    async fetchSession() {
      const positionName = localStorage.getItem("CorePositionName") || "销售";
      const cachedFamily = localStorage.getItem("CorePositionFamily");
      const inferredFamily = this.inferFamily(positionName);
      const positionFamily = !cachedFamily || cachedFamily === "GENERAL" ? inferredFamily : cachedFamily;
      const session = await coreapi.synchronize({
        employeeName: localStorage.getItem("CoreEmployeeName") || "当前员工",
        departmentKey: localStorage.getItem("CoreDepartmentKey") || localStorage.getItem("GovDepSysno") || "DP-SAFE",
        departmentName: localStorage.getItem("CoreDepartmentName") || "安全事业部",
        positionName,
        positionFamily,
        supervisor: localStorage.getItem("isTop") === "1" || localStorage.getItem("CoreSupervisor") === "true"
      });
      writePageCache("session", session);
      this.applyEmployeeSession(session);
      localStorage.setItem("CorePositionName", session.positionName || positionName);
      localStorage.setItem("CorePositionFamily", session.positionFamily || positionFamily);
      localStorage.setItem("CoreDepartmentName", session.departmentName || localStorage.getItem("CoreDepartmentName") || "");
      localStorage.setItem("CoreBrandPost", session.brandPost || "");
      localStorage.setItem("isBoss", session.boss ? "1" : "0");
      localStorage.setItem("CoreIsBoss", session.boss ? "true" : "false");
      window.dispatchEvent(new CustomEvent("brand-post-updated"));
      return session;
    },
    applyEmployeeSession(session) {
      if (!session) return;
      const current = this.employee || {};
      this.employee = Object.assign({}, current, {
        employeeId: session.employeeId || current.employeeId,
        employeeName: session.employeeName || current.employeeName,
        departmentName: session.departmentName || current.departmentName,
        positionName: session.positionName || current.positionName,
        positionFamily: session.positionFamily || current.positionFamily,
        supervisor: session.supervisor,
        boss: session.boss,
        brandPost: session.brandPost || current.brandPost
      });
    },
    async loadPrimary() {
      const month = this.monthKey;
      const token = (this._monthToken = (this._monthToken || 0) + 1);
      const historyPromise = coreapi.history(month).catch(() => []);
      const data = await coreapi.workspace(month);
      if (token !== this._monthToken) return;
      this.employee = data.employee || this.employee;
      this.entries = data.entries || [];
      this.workCalendar = data.calendar || [];
      this.performance = data.performance || {};
      this.configurationWarnings = data.configurationWarnings || [];
      this.improvementSuggestions = data.improvementSuggestions || [];
      this.loading = false;
      this.persistEfficiency();
      const history = await historyPromise;
      if (token !== this._monthToken) return;
      this.logHistory = history || [];
      this.persistEfficiency();
    },
    async loadSecondary() {
      const token = this._monthToken;
      const values = await Promise.all([
        coreapi.tasks().catch(() => []),
        coreapi.availableTasks().catch(() => []),
        coreapi.notifications().catch(() => []),
        coreapi.corrections().catch(() => [])
      ]);
      if (token !== this._monthToken) return;
      const performanceTasks = values[0] || [];
      const projectTasks = (values[1] || []).map(row => ({
        id: row.id,
        title: row.title,
        status: row.status || row.STATUS || "ASSIGNED"
      }));
      this.tasks = performanceTasks.concat(projectTasks);
      this.notifications = values[2] || [];
      this.corrections = values[3] || [];
      if (this.isFinance) await this.loadFinancePending();
      if (token !== this._monthToken) return;
      this.persistEfficiency();
    },
    async loadFinancePending() {
      try {
        const counts = await coreapi.financeQuantitativePendingCounts();
        this.financePendingCount = Number(counts.SALES || 0) + Number(counts.BRAND || 0);
        const d = new Date();
        const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const summary = await coreapi.financeSettlementSummary(month);
        this.financeSettlementPending = Number(summary.pendingConfirm || 0);
      } catch (ignore) {
        this.financePendingCount = 0;
        this.financeSettlementPending = 0;
      }
    },
    openMaterial(item) {
      this.$router.push({name: "knowledge", query: {search: this.suggestionTitle(item), recommended: item.metricCode}});
    },
    suggestionTitle(item) {
      return this.dimensionLabel(item.metricCode) || item.metricName || item.metricCode;
    },
    /** 将后端 reason 中的 metricCode 替换为中文维度名 */
    suggestionReason(item) {
      const title = this.suggestionTitle(item);
      if (item.reason && item.metricCode && item.reason.indexOf(item.metricCode) >= 0) {
        return item.reason.split(item.metricCode).join(title);
      }
      return item.reason || "";
    },
    dimensionLabel(code) {
      return {
        metric_relevance: "量化指标相关性",
        work_fact_completeness: "工作事实完整度",
        result_data_clarity: "结果数据清晰度",
        execution_depth: "执行深度",
        evidence_traceability: "证据可追溯性",
        sales_process: "销售过程质量",
        channel_quality: "渠道开发质量",
        meeting_reception: "会务接待",
        requirement_solution_delivery: "需求分析、技术方案与按期交付",
        full_stack_implementation: "前后端功能开发、接口联调与代码质量",
        testing_release_quality: "测试、缺陷修复、发布与验收质量",
        stability_performance_security: "部署运维、稳定性、性能、数据与安全",
        collaboration_documentation: "跨部门协同、技术文档与知识沉淀",
        payment_review: "收支与报销审核",
        accounting_compliance: "账务税务与合规",
        reconciliation_assets: "对账与资产管理",
        reporting_support: "销售收款汇报与经营支持",
        reception_service: "接待服务",
        environment_service: "环境与会务保障",
        logistics_execution: "后勤执行与响应",
        recruitment_process: "招聘过程与到岗转化",
        retention_management: "员工留存跟进与风险处置",
        personnel_compliance: "人事手续与协同质量",
        market_user_research: "市场调研、用户需求与产品规划",
        project_delivery: "项目全流程进度、质量与风险管理",
        testing_acceptance: "产品测试、功能校验与上线验收",
        ai_innovation: "AI与大模型场景融合及创新落地",
        cross_department_delivery: "跨部门协同、上线与常态化运营",
        ui_interface_design: "Web、H5、小程序及后台UI界面设计",
        interaction_visual_solution: "需求讨论、交互与视觉方案",
        prototype_high_fidelity: "产品原型到高保真设计稿转化",
        implementation_acceptance: "前端还原配合与视觉验收",
        design_system: "UI规范、组件、色彩与字体体系",
        ux_optimization: "基于数据反馈的用户体验优化",
        graphic_vi_material: "海报、VI及宣传物料设计"
      }[code] || "";
    },
    isSubmitted(date, cadence) {
      return this.entries.some(item => item.date === date && item.cadence === cadence && item.status === "SUBMITTED");
    },
    isRejected(date, cadence) {
      return this.entries.some(item => item.date === date && item.cadence === cadence && item.status === "DRAFT");
    },
    changeMonth(offset) {
      const tasks = this.tasks;
      const notifications = this.notifications;
      const corrections = this.corrections;
      const financePendingCount = this.financePendingCount;
      const financeSettlementPending = this.financeSettlementPending;
      this.viewDate = new Date(this.currentYear, this.currentMonth - 1 + offset, 1);
      const painted = this.restoreEfficiency(this.monthKey);
      this.tasks = tasks;
      this.notifications = notifications;
      this.corrections = corrections;
      this.financePendingCount = financePendingCount;
      this.financeSettlementPending = financeSettlementPending;
      if (!painted) {
        this.entries = [];
        this.workCalendar = [];
        this.logHistory = [];
        this.performance = {};
        this.configurationWarnings = [];
        this.improvementSuggestions = [];
      }
      this.loadPrimary().catch(e => this.$toast.fail(e.message));
    },
    /** 日报允许补填往日工作日；未来日期和休息日拦截；周报/月报走 WorklogForm */
    openLog(cadence, date) {
      if (cadence === "DAILY") {
        if (date > this.today) {
          this.$toast("未来日期暂不能填写日志");
          return
        }
        const day = this.workCalendar.find(item => item.date === date);
        if (day && !day.workday) {
          this.$toast("休息日无需提交日志");
          return
        }
      }
      this.$router.push({name: "EfficiencyLog", query: {cadence, date}});
    },
    formatMetric(value) {
      return Number(value || 0).toLocaleString("zh-CN", {maximumFractionDigits: 2});
    },
    formatRate(value) {
      return value == null ? "--" : `${Math.round(Number(value))}%`;
    },
    formatWeight(value) {
      return `${Math.round(Number(value) * 100)}%权重`;
    },
    /** 岗位名 → positionFamily，缓存缺失时本地推断 */
    inferFamily(name) {
      const n = name || "";
      if (n.indexOf("店长") >= 0) return "REHAB_MANAGER";
      const values = {
        "总负责人": "GENERAL_MANAGER",
        "康复中心店长": "REHAB_MANAGER",
        "心理咨询师": "PSYCHOLOGICAL_COUNSELOR",
        "推拿理疗师": "MASSAGE_THERAPIST",
        "网培管理岗": "TRAINING_MANAGER",
        "财务": "FINANCE",
        "总经办财务岗": "FINANCE",
        "品牌运营岗": "BRAND",
        "品牌运营": "BRAND",
        "全栈": "FULL_STACK",
        "技术中心-全栈": "FULL_STACK",
        "销售": "SALES",
        "网培招商 / 招生岗": "SALES"
      };
      return values[name] || "GENERAL";
    }, progressWidth(metric) {
      return `${Math.min(100, Math.max(0, Number(metric.completionRate || 0)))}%`;
    },
    formatDate(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    }
  }
};
</script>

<style lang="less" scoped>
@import "~@/assets/base.less";

.efficiency-page {
  min-height: 100vh;
  padding-bottom: 1.65rem;
  background: #f6f7fb;
  color: #333
}

.employee-card {
  margin: .3rem .28rem;
  padding: .4rem;
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: .24rem;
  box-shadow: 0 .08rem .3rem rgba(211, 47, 47, .1)
}

.avatar {
  width: 1.08rem;
  height: 1.08rem;
  line-height: 1.08rem;
  flex: 0 0 1.08rem;
  text-align: center;
  color: #fff;
  font-size: .46rem;
  font-weight: 700;
  border-radius: 50%;
  background: linear-gradient(145deg, #D32F2F, #1A1A1A)
}

.employee-main {
  min-width: 0;
  margin-left: .3rem;
  flex: 1
}

.employee-main h1 {
  font-size: .42rem;
  letter-spacing: 0
}

.employee-main p {
  margin-top: .08rem;
  color: #8a8da3;
  font-size: .3rem
}

.position-tag {
  padding: .12rem .24rem;
  color: @color;
  font-size: .3rem;
  background: #fff5f5;
  border-radius: .3rem
}

.action-panel {
  margin: 0 .28rem .3rem;
  display: flex;
  overflow: hidden;
  background: #fff;
  border-radius: .24rem;
  box-shadow: 0 .08rem .3rem rgba(211, 47, 47, .1)
}

.date-block {
  width: 2rem;
  flex: 0 0 2rem;
  padding: .45rem .25rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #fff;
  background: linear-gradient(145deg, #D32F2F, #B71C1C)
}

.date-block strong {
  font-size: .4rem
}

.date-block span {
  margin-top: .08rem;
  font-size: .28rem;
  opacity: .82
}

.action-list {
  flex: 1;
  padding: .15rem .3rem
}

.action-row {
  min-height: 1.22rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1PX solid #eee
}

.action-row:first-child {
  border-top: 0
}

.action-row > div {
  min-width: 0;
  flex: 1 1 auto
}

.action-row b {
  display: flex;
  align-items: flex-start;
  font-size: .34rem;
  max-width: 100%
}

.action-row .status-label {
  display: block;
  margin-top: .05rem;
  font-size: .27rem
}

.action-row .action-btn {
  flex: 0 0 auto;
  margin-left: .15rem
}

.pending {
  color: #f35d5d
}

.done {
  color: #32ad68
}

.kpi-panel, .calendar-card {
  margin: 0 .28rem .3rem;
  padding: .34rem .32rem;
  background: #fff;
  border-radius: .24rem;
  box-shadow: 0 .08rem .3rem rgba(211, 47, 47, .1)
}

.kpi-panel header {
  display: flex;
  align-items: center;
  justify-content: space-between
}

.kpi-panel h2 {
  font-size: .38rem
}

.kpi-panel header p {
  margin-top: .05rem;
  color: #9799aa;
  font-size: .26rem
}

.kpi-panel header > span {
  padding: .1rem .2rem;
  border-radius: .24rem;
  font-size: .27rem
}

.kpi-panel .achieved {
  color: #248d55;
  background: #e8f8ee
}

.kpi-panel .progressing {
  color: #d77a20;
  background: #fff3e5
}

.kpi-list {
  margin-top: .2rem
}

.kpi-item {
  display: grid;
  grid-template-columns:2.55rem minmax(0, 1fr) .8rem;
  align-items: center;
  gap: .18rem;
  padding: .22rem 0;
  border-top: 1PX solid #f0f0f3
}

.kpi-copy b, .kpi-copy span {
  display: block
}

.kpi-copy b {
  font-size: .31rem
}

.kpi-copy span {
  margin-top: .04rem;
  color: #8a8c9c;
  font-size: .25rem
}

.progress-track {
  height: .14rem;
  overflow: hidden;
  border-radius: .08rem;
  background: #f0f0f0
}

.progress-track i {
  display: block;
  height: 100%;
  border-radius: .08rem;
  background: #4eac78
}

.kpi-item strong {
  text-align: right;
  color: #55586b;
  font-size: .28rem
}

.calendar-card {
  margin-bottom: 0
}

.calendar-card header {
  height: .8rem;
  display: flex;
  align-items: center;
  justify-content: space-between
}

.calendar-card header button {
  width: .8rem;
  height: .8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #73768a;
  font-size: .38rem
}

.calendar-card header strong {
  display: block;
  line-height: .8rem;
  font-size: .4rem
}

.week-row, .calendar-grid {
  display: grid;
  grid-template-columns:repeat(7, minmax(0, 1fr))
}

.week-row {
  margin-top: .18rem;
  color: #9a9caf;
  text-align: center;
  font-size: .28rem
}

.calendar-grid {
  margin-top: .15rem;
  row-gap: .08rem
}

.calendar-grid button {
  position: relative;
  width: .78rem;
  height: .78rem;
  justify-self: center;
  color: #505267;
  border-radius: .16rem;
  font-size: .31rem
}

.calendar-grid button.outside {
  color: #d6d7df
}

.calendar-grid button.future {
  color: #c9cad3
}

.calendar-grid button.submitted {
  color: #278e56;
  background: #e9f8ef
}

.calendar-grid button.missing {
  color: #d84d4d;
  background: #fff2f2
}

.calendar-grid button.today {
  color: @color;
  border: 1PX solid @color;
  font-weight: 700;
  background: #fff5f5
}

.calendar-grid button.today.submitted, .calendar-grid button.submitted.today {
  color: #278e56;
  border: 1PX solid #278e56;
  background: #e9f8ef;
  font-weight: 700
}

.check {
  position: absolute;
  right: .04rem;
  top: 0;
  font-size: .22rem;
  font-style: normal
}

.dot {
  position: absolute;
  bottom: .08rem;
  left: 50%;
  width: .08rem;
  height: .08rem;
  margin-left: -.04rem;
  border-radius: 50%;
  background: #D32F2F
}

.legend {
  margin-top: .3rem;
  display: flex;
  justify-content: center;
  gap: .45rem;
  color: #888b9c;
  font-size: .27rem
}

.legend span {
  display: flex;
  align-items: center
}

.legend i {
  width: .2rem;
  height: .2rem;
  margin-right: .1rem;
  border-radius: .06rem
}

.legend .submitted {
  background: #bde9cf
}

.legend .missing {
  background: #ffc9c9
}

.legend .today {
  border: 1PX solid @color
}

.page-loading {
  position: fixed;
  left: 50%;
  top: 48%;
  transform: translate(-50%, -50%);
  padding: .3rem;
  border-radius: .2rem;
  background: rgba(255, 255, 255, .95);
  box-shadow: 0 .08rem .3rem rgba(0, 0, 0, .1)
}

.notice-panel {
  margin: 0 .28rem .3rem;
  padding: .3rem .32rem;
  color: #9a651f;
  font-size: .28rem;
  line-height: 1.55;
  background: #fff8e8;
  border-left: .08rem solid #e8a43a;
  border-radius: .16rem
}

.quick-panel, .suggestion-panel {
  margin: 0 .28rem .3rem;
  padding: .32rem;
  background: #fff;
  border-radius: .24rem;
  box-shadow: 0 .08rem .3rem rgba(211, 47, 47, .1)
}

.quick-panel button {
  width: 100%;
  display: flex;
  align-items: center;
  color: #B71C1C;
  font-size: .46rem
}

.quick-panel span {
  margin-left: .25rem;
  flex: 1;
  text-align: left
}

.quick-panel b, .quick-panel small {
  display: block
}

.quick-panel b {
  color: #34364a;
  font-size: .34rem
}

.quick-panel small {
  margin-top: .05rem;
  color: #9295a7;
  font-size: .26rem
}

.suggestion-panel header {
  display: flex;
  justify-content: space-between;
  color: #D32F2F;
  font-size: .45rem
}

.suggestion-panel h2 {
  color: #34364a;
  font-size: .38rem
}

.suggestion-panel header p {
  margin-top: .04rem;
  color: #9698a8;
  font-size: .25rem
}

.suggestion-panel article {
  padding: .25rem 0;
  display: flex;
  align-items: center;
  border-top: 1PX solid #eee
}

.suggestion-panel article:first-of-type {
  margin-top: .2rem
}

.suggestion-panel article div {
  min-width: 0;
  flex: 1
}

.suggestion-panel article b, .suggestion-panel article span {
  display: block
}

.suggestion-panel article b {
  font-size: .31rem
}

.suggestion-panel article span {
  margin-top: .05rem;
  color: #8b8d9d;
  font-size: .25rem;
  line-height: 1.45
}

.suggestion-panel article button {
  margin-left: .2rem;
  padding: .14rem .2rem;
  color: #fff;
  border-radius: .12rem;
  background: #B71C1C;
  font-size: .25rem
}

.suggestion-panel article em {
  margin-left: .2rem;
  color: #aaa;
  font-size: .24rem;
  font-style: normal
}

.personal-entry {
  margin: 0 .28rem .3rem;
  padding: .3rem;
  background: #fff;
  border-radius: .24rem;
  box-shadow: 0 .08rem .3rem rgba(211, 47, 47, .1);
  overflow: hidden
}

.personal-entry h2 {
  margin: 0;
  color: #34364a;
  font-size: .38rem
}

.personal-entry > div {
  margin-top: .22rem;
  display: grid;
  grid-template-columns:1fr;
  gap: .12rem
}

.personal-entry button {
  height: .92rem;
  border: 0;
  color: @color;
  background: #fff5f5;
  border-radius: .14rem;
  font-size: .28rem
}

.personal-entry button .van-icon {
  margin-right: .1rem;
  font-size: .32rem;
  vertical-align: -.04rem
}

.execution-entry {
  margin: 0 .28rem .3rem;
  padding: .3rem;
  background: #fff;
  border-radius: .24rem;
  box-shadow: 0 .08rem .3rem rgba(211, 47, 47, .1)
}

.execution-entry header {
  padding: 0 .03rem .18rem
}

.execution-entry h2 {
  font-size: .38rem
}

.execution-entry header p {
  margin-top: .05rem;
  color: #9295a7;
  font-size: .25rem
}

.execution-entry button {
  width: 100%;
  min-height: 1.15rem;
  display: flex;
  align-items: center;
  border-top: 1PX solid #eee;
  text-align: left;
  background: #fff
}

.execution-entry button > .van-icon:first-child {
  width: .7rem;
  color: #d32f2f;
  font-size: .4rem
}

.execution-entry button > span {
  min-width: 0;
  flex: 1
}

.execution-entry b, .execution-entry small {
  display: block
}

.execution-entry b {
  font-size: .31rem
}

.execution-entry small {
  margin-top: .05rem;
  color: #9295a7;
  font-size: .24rem
}

.execution-entry button > .van-icon:last-child {
  color: #b0b2c0
}

.nav-tools {
  height: .96rem;
  display: flex;
  align-items: center;
  gap: .1rem
}

.nav-tools button {
  width: .8rem;
  height: .8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: .5rem;
  background: transparent
}

.nav-tools button .van-icon {
  color: #fff;
  -webkit-text-stroke: .35PX #fff;
  text-shadow: 0 0 .01rem #fff
}

.nav-tools button:active {
  opacity: .65
}

.task-notice {
  margin: 0 .28rem .3rem;
  padding: .25rem .28rem;
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: .2rem;
  box-shadow: 0 .08rem .3rem rgba(211, 47, 47, .1)
}

.task-icon {
  position: relative;
  width: .7rem;
  color: @color;
  font-size: .43rem
}

.task-icon i {
  position: absolute;
  right: .02rem;
  top: -.14rem;
  min-width: .3rem;
  height: .3rem;
  padding: 0 .04rem;
  line-height: .3rem;
  color: #fff;
  text-align: center;
  font-size: .18rem;
  font-style: normal;
  border-radius: .2rem;
  background: #D32F2F
}

.task-notice > span {
  min-width: 0;
  flex: 1
}

.task-notice b, .task-notice small {
  display: block
}

.task-notice b {
  font-size: .3rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap
}

.task-notice small {
  margin-top: .05rem;
  color: #9295a7;
  font-size: .24rem
}

.task-notice > .van-icon {
  color: #9799aa;
  font-size: .3rem
}

.calendar-grid button.rest {
  color: #aeb0bd;
  background: #f1f2f5
}

.rest-mark {
  position: absolute;
  right: .03rem;
  bottom: .01rem;
  color: #a5a7b3;
  font-size: .16rem;
  font-style: normal
}

.legend {
  gap: .25rem;
  font-size: .25rem
}

.legend .rest {
  background: #e2e3e8
}

.rest-text {
  color: #999
}

.reject {
  color: #D32F2F;
  font-weight: 600
}

.action-row .action-left {
  min-width: 0;
  flex: 1 1 auto;
  max-width: 100%;
  overflow: hidden
}

.action-row b .action-title {
  flex: 0 0 auto;
  white-space: nowrap;
  padding-right: .1rem;
  font-size: inherit
}

.action-row .reject-reason {
  flex: 1 1 auto;
  min-width: 0;
  display: block
}

.action-row .reject-reason-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  color: #999;
  font-size: .26rem;
  font-weight: 400;
  line-height: 1.4;
  width: 100%
}

.boss-efficiency-panel {
  margin: 0 .28rem .3rem;
  padding: .32rem;
  background: #fff;
  border-radius: .24rem;
  box-shadow: 0 .08rem .3rem rgba(211, 47, 47, .1)
}

.boss-efficiency-panel > header {
  padding: .04rem .04rem .24rem;
  display: flex;
  align-items: center;
  justify-content: space-between
}

.boss-efficiency-panel > header h2 {
  color: #34364a;
  font-size: .38rem
}

.boss-efficiency-panel > header p {
  margin-top: .06rem;
  color: #9295a7;
  font-size: .25rem
}

.boss-efficiency-panel > header > .van-icon {
  width: .68rem;
  height: .68rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: @color;
  font-size: .38rem;
  background: #fff5f5;
  border-radius: .16rem
}

.boss-efficiency-panel > button {
  width: 100%;
  min-height: 1.28rem;
  padding: .22rem .04rem;
  display: flex;
  align-items: center;
  border-top: 1PX solid #eee;
  text-align: left;
  background: #fff
}

.boss-efficiency-panel .entry-icon {
  width: .76rem;
  height: .76rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 .76rem;
  color: #fff;
  font-size: .4rem;
  background: @color;
  border-radius: .17rem
}

.boss-efficiency-panel > button:nth-of-type(2) .entry-icon {
  color: #2d9561;
  background: #eaf7f0
}

.boss-efficiency-panel > button:nth-of-type(3) .entry-icon {
  color: #1667c2;
  background: #eaf2fc
}

.boss-efficiency-panel > button > span:nth-child(2) {
  min-width: 0;
  margin-left: .2rem;
  flex: 1
}

.boss-efficiency-panel b, .boss-efficiency-panel small {
  display: block
}

.boss-efficiency-panel b {
  color: #34364a;
  font-size: .32rem
}

.boss-efficiency-panel small {
  margin-top: .06rem;
  color: #85899c;
  font-size: .24rem;
  line-height: 1.4
}

.boss-efficiency-panel > button > .van-icon:last-child {
  margin-left: .12rem;
  color: #b0b2c0;
  font-size: .28rem
}

.shared-rank-entries {
  margin: .3rem .28rem 0;
  display: grid;
  grid-template-columns:repeat(2, minmax(0, 1fr));
  gap: .16rem;
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  text-size-adjust: 100%;
}

.shared-rank-entries + .shared-rank-entries {
  margin-top: .16rem;
}

.shared-rank-entries button {
  width: 100%;
  min-width: 0;
  min-height: 1.42rem;
  height: auto;
  padding: .2rem .08rem;
  border: 0;
  border-radius: .2rem;
  overflow: hidden;
  color: #fff;
  background: #e1272f
}

.shared-rank-entries b, .shared-rank-entries small {
  display: block;
  max-width: 100%;
  overflow: hidden;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap
}

.shared-rank-entries b {
  font-size: .32rem
}

.shared-rank-entries small {
  margin-top: .08rem;
  font-size: .25rem
}

.shared-rank-entries .muted {
  background: #707070;
  opacity: 1
}
</style>



// WEBPACK FOOTER //
// src/views/efficiency/Efficiency.vue